import {
  Chip,
  ClickAwayListener,
  Fab,
  Fade,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import React, { useContext, useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  AbsoluteContainer,
  Image,
  RelativeContainer,
} from "../styled/Container.styled";
import { HiddenInput } from "../styled/Form.styled";
import { ConfiguratorContext } from "../../../contexts/ConfiguratorContext";
import { downloadFile } from "../../../data/configAPI";
import { PillButton } from "../styled/Button.styled";
import ImageGuidelinesTooltip from "./ImageGuidelinesTooltip";
import {
  BANNER_IMAGE_DASHBOARD_KEY,
  BANNER_IMAGE_PAYMENTS_KEY,
  BANNER_IMAGE_STATEMENTS_KEY,
  BANNER_URL_DASHBOARD_KEY,
  BANNER_URL_PAYMENTS_KEY,
  BANNER_URL_STATEMENTS_KEY,
} from "../../constants/Configurator.constants";
import { CLIENT_ADMIN_ROLE } from "../../constants/Roles.constants";
import { UPLOAD_SIZE_EXCEEDED } from "../../constants/StatementsDocuments.constants";
import ActionSnackbar from "../ActionSnackbar";

const BANNER_PAGES = [
  { imageKey: BANNER_IMAGE_DASHBOARD_KEY, urlKey: BANNER_URL_DASHBOARD_KEY },
  { imageKey: BANNER_IMAGE_PAYMENTS_KEY, urlKey: BANNER_URL_PAYMENTS_KEY },
  { imageKey: BANNER_IMAGE_STATEMENTS_KEY, urlKey: BANNER_URL_STATEMENTS_KEY },
];

const MarketingBanner = ({
  elementKey = "",
  hyperlinkElementKey = "",
  size = 5,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { token, userRoles, visibleRole, impersonatedUser } =
    useContext(AuthContext);
  const { edittedFields, addNewEdittedField, addBatchOfEdittedFields } =
    useContext(ConfiguratorContext);

  const [selectedFile, setSelectedFile] = useState(null);
  const [renderedImage, setRenderedImage] = useState();

  const [hyperlinkPopupOpen, setHyperlinkPopupOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState(null);
  const [imageURL, setImageURL] = useState("");
  const [formImageURL, setFormImageURL] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [copySuccessOpen, setCopySuccessOpen] = useState(false);

  useEffect(() => {
    const getBannerData = async () => {
      if (token) {
        const currentBannerURL = edittedFields.find(
          (item) => item.key === hyperlinkElementKey,
        );
        if (currentBannerURL) {
          setImageURL(currentBannerURL.value);
          setFormImageURL(currentBannerURL.value);
        }

        const currentBannerImage = edittedFields.find(
          (item) => item.key === elementKey,
        );
        if (currentBannerImage) {
          setSelectedFile(currentBannerImage.value);
        } else {
          callImageApi(token);
        }
      }
    };
    getBannerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!selectedFile) {
      setRenderedImage(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setRenderedImage(objectUrl);


    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const callImageApi = async (token) => {
    const imageData = await downloadFile(token, elementKey, impersonatedUser);
    if (imageData?.type?.includes("image")) {
      addNewEdittedField({
        key: elementKey,
        value: imageData,
        modified: false,
      });
      setSelectedFile(imageData);
    }
  };

  const uploadBanner = (event) => {
    const newFile = event?.target?.files?.[0];
    if (newFile) {
      if (newFile.size <= size * 1e6) {
        addNewEdittedField({
          key: elementKey,
          value: event.target.files[0],
          modified: true,
        });
        setSelectedFile(event?.target?.files?.[0]);
      } else {
        setSnackbarMessage(UPLOAD_SIZE_EXCEEDED(size));
        setSnackbarOpen(true);
      }
    }
  };

  const openHyperlinkForm = (e) => {
    setAnchorElement(e.currentTarget);
    setHyperlinkPopupOpen(true);
  };

  const handleHyperlinkFormClose = (e) => {
    if (anchorElement.current && anchorElement.current.contains(e.target)) {
      return;
    }

    setHyperlinkPopupOpen(false);
  };

  const submitImageHyperLinkForm = (e) => {
    if (e.key === "Enter") {
      addImageHyperlink();
    }
  };

  const addImageHyperlink = () => {
    setHyperlinkPopupOpen(false);
    addNewEdittedField({
      key: hyperlinkElementKey,
      value: formImageURL,
      modified: true,
    });
    setImageURL(formImageURL);
  };

  const bannerAction = () => {
    if (imageURL) {
      const nativeWindow = window.open(
        `${imageURL.substring(0, 4) !== "http" ? "http://" : ""}${imageURL}`,
        "_blank",
      );
      nativeWindow.focus();
    }
  };

  const copyToAllPages = () => {
    const otherPages = BANNER_PAGES.filter((p) => p.imageKey !== elementKey);
    const batch = [];
    otherPages.forEach(({ imageKey, urlKey }) => {
      if (selectedFile) {
        batch.push({ key: imageKey, value: selectedFile, modified: true });
      }
      batch.push({
        key: urlKey,
        value: imageURL || "",
        modified: true,
      });
    });
    if (batch.length > 0) {
      addBatchOfEdittedFields(batch);
      setCopySuccessOpen(true);
    }
  };

  return selectedFile ||
    (userRoles?.includes(CLIENT_ADMIN_ROLE) &&
      visibleRole === CLIENT_ADMIN_ROLE) ? (
    <>
      <RelativeContainer
        width="100%"
        border={`1px solid ${theme.palette.text.hint}`}
        aspectratio={isMobile ? "3/1" : "10/2"}
        sx={{
          minHeight: isMobile ? '120px' : 'auto',
          background: theme.palette.background.paper
        }}
      >
        {selectedFile ? (
          <Image
            src={renderedImage}
            onClick={(e) => bannerAction()}
            sx={{ 
              cursor: imageURL ? "pointer" : "default",
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt="banner"
          />
        ) : (
          <Image
            src="https://via.placeholder.com/1200x240/007bff/ffffff?text=Dashboard+Marketing+Banner"
            onClick={(e) => bannerAction()}
            sx={{ 
              cursor: imageURL ? "pointer" : "default",
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt="banner placeholder"
          />
        )}
        {userRoles?.includes(CLIENT_ADMIN_ROLE) &&
        visibleRole === CLIENT_ADMIN_ROLE ? (
          <>
            <AbsoluteContainer sx={{ 
              top: isMobile ? -5 : -10, 
              right: isMobile ? -20 : -35, 
              zIndex: 4 
            }}>
              <Tooltip title="Select Banner Image" placement="left">
                <label htmlFor="icon-button-file">
                  <HiddenInput
                    accept="image/*"
                    id="icon-button-file"
                    type="file"
                    onChange={uploadBanner}
                    value=""
                  />
                  <Fab
                    color="primary"
                    size={isMobile ? "small" : "small"}
                    sx={{ 
                      width: isMobile ? 24 : 30, 
                      height: isMobile ? 24 : 30, 
                      minHeight: isMobile ? 24 : 30 
                    }}
                    component="span"
                  >
                    <FileUploadRoundedIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                  </Fab>
                </label>
              </Tooltip>
            </AbsoluteContainer>
            {selectedFile && (
              <>
                <AbsoluteContainer sx={{ 
                  top: isMobile ? -5 : -10, 
                  left: isMobile ? -15 : -25, 
                  zIndex: 4 
                }}>
                  <ImageGuidelinesTooltip ratio={isMobile ? "3:1" : "10:2"} placement="right" />
                </AbsoluteContainer>
                <AbsoluteContainer sx={{ 
                  top: isMobile ? 15 : 25, 
                  right: isMobile ? -20 : -35, 
                  zIndex: 4 
                }}>
                  <Tooltip title="Add Redirect URL" placement="left">
                    <Fab
                      color="secondary"
                      size={isMobile ? "small" : "small"}
                      sx={{ 
                        width: isMobile ? 24 : 30, 
                        height: isMobile ? 24 : 30, 
                        minHeight: isMobile ? 24 : 30 
                      }}
                      onClick={openHyperlinkForm}
                    >
                      <LinkIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                    </Fab>
                  </Tooltip>
                  <Popper
                    open={hyperlinkPopupOpen}
                    anchorEl={anchorElement}
                    placement="bottom-start"
                    transition
                    sx={{
                      zIndex: 1300,
                      width: isMobile ? '250px' : 'auto',
                    }}
                  >
                    {({ TransitionProps }) => (
                      <Fade {...TransitionProps} timeout={350}>
                        <Paper sx={{ padding: isMobile ? 1 : 2 }}>
                          <ClickAwayListener
                            onClickAway={handleHyperlinkFormClose}
                          >
                            <Stack spacing={isMobile ? 1 : 2}>
                              <TextField
                                value={formImageURL}
                                onChange={(e) =>
                                  setFormImageURL(e.target.value)
                                }
                                onKeyUp={submitImageHyperLinkForm}
                                label="Link Address (URL)"
                                variant="standard"
                                size={isMobile ? "small" : "medium"}
                              />
                              <PillButton
                                variant="contained"
                                onClick={addImageHyperlink}
                                onKeyUp={submitImageHyperLinkForm}
                                sx={{
                                  fontSize: isMobile ? '0.875rem' : '1rem',
                                }}
                              >
                                Add
                              </PillButton>
                            </Stack>
                          </ClickAwayListener>
                        </Paper>
                      </Fade>
                    )}
                  </Popper>
                </AbsoluteContainer>
                <AbsoluteContainer sx={{ 
                  bottom: isMobile ? 2 : 5, 
                  right: isMobile ? 2 : 5, 
                  zIndex: 4 
                }}>
                  <Chip
                    color={imageURL ? "success" : "error"}
                    size={isMobile ? "small" : "small"}
                    sx={{
                      backgroundColor: imageURL
                        ? theme.palette.success.light
                        : theme.palette.error.light,
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                    }}
                    label={
                      imageURL ? (
                        <LinkIcon
                          fontSize={isMobile ? "small" : "small"}
                          sx={{ verticalAlign: "bottom" }}
                        />
                      ) : (
                        <LinkOffIcon
                          fontSize={isMobile ? "small" : "small"}
                          sx={{ verticalAlign: "bottom" }}
                        />
                      )
                    }
                  />
                </AbsoluteContainer>
                <AbsoluteContainer sx={{ 
                  bottom: isMobile ? 2 : 5, 
                  left: isMobile ? 2 : 5, 
                  zIndex: 4 
                }}>
                  <Tooltip title="Copy this banner image and link to Dashboard, Payments, and Statements and Documents" placement="top">
                    <span>
                      <PillButton
                        size="small"
                        variant="outlined"
                        onClick={copyToAllPages}
                        startIcon={<ContentCopyIcon sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }} />}
                        sx={{
                          fontSize: isMobile ? '0.7rem' : '0.75rem',
                          py: 0.5,
                          px: 1,
                        }}
                      >
                        Copy to all pages
                      </PillButton>
                    </span>
                  </Tooltip>
                </AbsoluteContainer>
              </>
            )}
          </>
        ) : (
          ""
        )}
      </RelativeContainer>
      <ActionSnackbar
        severity="error"
        borderColor={theme.palette.error.main}
        vertical="top"
        horizontal="center"
        width={isMobile ? "90%" : "50%"}
        isOpen={snackbarOpen}
        closeFn={() => {
          setSnackbarOpen(false);
        }}
        snackbarLabel={snackbarMessage}
        hasAction={false}
        autoHideDuration={6000}
      />
      <ActionSnackbar
        severity="success"
        borderColor={theme.palette.success.main}
        vertical="bottom"
        horizontal="center"
        width={isMobile ? "90%" : "40%"}
        isOpen={copySuccessOpen}
        closeFn={() => setCopySuccessOpen(false)}
        snackbarLabel="Image and link copied to all pages (Dashboard, Payments, Statements and Documents)"
        hasAction={false}
        autoHideDuration={4000}
      />
    </>
  ) : (
    ""
  );
};

export default MarketingBanner;